import React, { useMemo, useState } from "react";
import { API_BASE_URL } from "../App";
import "../styles/MainPage.css";

const SORT_COLUMNS = [
  { value: "JerseyNumber", label: "Jersey #" },
  { value: "LegalFirstName", label: "First Name" },
  { value: "LastName", label: "Last Name" },
  { value: "PositionName", label: "Position" },
  { value: "FirstChoiceVotes", label: "1st Votes" },
  { value: "SecondChoiceVotes", label: "2nd Votes" },
  { value: "ThirdChoiceVotes", label: "3rd Votes" },
  { value: "CoachVotes", label: "Coach Votes" },
  { value: "OverallScore", label: "Overall Score" },
];

function WatchingPage({ watching = [], setWatching = () => {} }) {
  const [team1Id, setTeam1Id] = useState("");
  const [team2Id, setTeam2Id] = useState("");
  const [teamPlayersById, setTeamPlayersById] = useState({});
  const [loadingByTeam, setLoadingByTeam] = useState({});
  const [errorByTeam, setErrorByTeam] = useState({});
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [showNoVotesTeam1, setShowNoVotesTeam1] = useState(true);
  const [showNoVotesTeam2, setShowNoVotesTeam2] = useState(true);
  const [sortColumn, setSortColumn] = useState("JerseyNumber");
  const [sortDirection, setSortDirection] = useState("asc");
  const [actionStatus, setActionStatus] = useState(null);
  const [showWatchListModal, setShowWatchListModal] = useState(false);

  const watchedTeams = useMemo(() => {
    const deduped = [];
    const seen = new Set();

    watching.forEach((team) => {
      if (team?.TeamXID && !seen.has(team.TeamXID)) {
        seen.add(team.TeamXID);
        deduped.push(team);
      }
    });

    return deduped.sort((a, b) => String(a.TeamName).localeCompare(String(b.TeamName)));
  }, [watching]);

  const getCoachVotePoints = (coachRank) => {
    const rank = Number(coachRank) || 0;
    if (rank === 1) return 6;
    if (rank === 2) return 4;
    if (rank === 3) return 2;
    return 0;
  };

  const getOverallScore = (player) =>
    (Number(player.FirstChoiceVotes) || 0) * 3 +
    (Number(player.SecondChoiceVotes) || 0) * 2 +
    (Number(player.ThirdChoiceVotes) || 0) * 1 +
    getCoachVotePoints(player.CoachVotes);

  const hasVotes = (player) =>
    (Number(player.FirstChoiceVotes) || 0) > 0 ||
    (Number(player.SecondChoiceVotes) || 0) > 0 ||
    (Number(player.ThirdChoiceVotes) || 0) > 0 ||
    (Number(player.CoachVotes) || 0) > 0;

  const sortPlayers = (players) => {
    const sorted = [...players];

    sorted.sort((a, b) => {
      const aValue =
        sortColumn === "OverallScore" ? getOverallScore(a) : a[sortColumn] ?? "";
      const bValue =
        sortColumn === "OverallScore" ? getOverallScore(b) : b[sortColumn] ?? "";

      const aNumber = Number(aValue);
      const bNumber = Number(bValue);
      const bothNumeric = !Number.isNaN(aNumber) && !Number.isNaN(bNumber);

      let result;
      if (bothNumeric) {
        result = aNumber - bNumber;
      } else {
        result = String(aValue).localeCompare(String(bValue));
      }

      return sortDirection === "asc" ? result : -result;
    });

    return sorted;
  };

  const showStatus = (msg, isError = false) => {
    setActionStatus({ msg, isError });
    setTimeout(() => setActionStatus(null), 3000);
  };

  const fetchTeamRoster = async (teamId) => {
    if (!teamId || teamPlayersById[teamId] || loadingByTeam[teamId]) return;

    setLoadingByTeam((prev) => ({ ...prev, [teamId]: true }));
    setErrorByTeam((prev) => ({ ...prev, [teamId]: null }));

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/team-roster?teamXID=${encodeURIComponent(teamId)}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setTeamPlayersById((prev) => ({ ...prev, [teamId]: data }));
    } catch (error) {
      console.error("Error fetching watched team roster:", error);
      setErrorByTeam((prev) => ({
        ...prev,
        [teamId]: "Failed to load team roster.",
      }));
    } finally {
      setLoadingByTeam((prev) => ({ ...prev, [teamId]: false }));
    }
  };

  const handleTeamSelect = async (slot, teamId) => {
    if (slot === 1) {
      setTeam1Id(teamId);
    } else {
      setTeam2Id(teamId);
    }

    if (teamId) {
      await fetchTeamRoster(teamId);
    }
  };

  const removeTeamFromWatchList = (teamId) => {
    setWatching((prev) => prev.filter((team) => team.TeamXID !== teamId));

    if (team1Id === teamId) setTeam1Id("");
    if (team2Id === teamId) setTeam2Id("");

    const roster = teamPlayersById[teamId] || [];
    const rosterIds = new Set(roster.map((player) => player.PlayerXID));
    setSelectedPlayers((prev) => prev.filter((playerId) => !rosterIds.has(playerId)));
  };

  const handleSelectPlayer = (playerXID) => {
    if (!playerXID) return;
    setSelectedPlayers((prev) =>
      prev.includes(playerXID) ? prev.filter((id) => id !== playerXID) : [...prev, playerXID]
    );
  };

  const handleAddSelectedToAllTournament = async () => {
    if (selectedPlayers.length === 0) return;

    try {
      await Promise.all(
        selectedPlayers.map(async (playerXID) => {
          const response = await fetch(`${API_BASE_URL}/api/player-details`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ playerXID, allTournament: true }),
          });

          if (!response.ok) {
            throw new Error(`Failed to update player ${playerXID}`);
          }
        })
      );

      setTeamPlayersById((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((teamId) => {
          updated[teamId] = (updated[teamId] || []).map((player) =>
            selectedPlayers.includes(player.PlayerXID)
              ? { ...player, All_Tournament: true }
              : player
          );
        });
        return updated;
      });

      showStatus(`${selectedPlayers.length} player(s) added to All Tournament.`);
      setSelectedPlayers([]);
    } catch (error) {
      console.error("Error updating selected players:", error);
      showStatus("Failed to update players.", true);
    }
  };

  const renderTeamRoster = (teamId, teamLabel, showNoVotes, setShowNoVotes) => {
    if (!teamId) {
      return <div className="empty-team-state">Select {teamLabel} from your watch list.</div>;
    }

    const teamMeta = watchedTeams.find((team) => team.TeamXID === teamId);
    const roster = teamPlayersById[teamId] || [];

    if (loadingByTeam[teamId]) {
      return <div className="team-loading">Loading {teamMeta?.TeamName || "team"} roster...</div>;
    }

    if (errorByTeam[teamId]) {
      return <div className="team-load-error">{errorByTeam[teamId]}</div>;
    }

    const filtered = showNoVotes ? roster : roster.filter(hasVotes);
    const sorted = sortPlayers(filtered);

    return (
      <div className="watch-roster-card">
        <div className="watch-roster-header">
          <h3>{teamMeta?.TeamName}</h3>
          <button
            className="toolbar-btn toolbar-btn-att"
            onClick={() => setShowNoVotes((prev) => !prev)}
          >
            {showNoVotes ? "Hide No Votes" : "Show No Votes"}
          </button>
        </div>
        <table>
          <thead>
            <tr>
              <th>Select</th>
              <th>ATT</th>
              <th>#</th>
              <th>First</th>
              <th>Last</th>
              <th>Pos</th>
              <th>1st</th>
              <th>2nd</th>
              <th>3rd</th>
              <th>Coach</th>
              <th>Overall</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan="11" style={{ textAlign: "center" }}>
                  No players to display.
                </td>
              </tr>
            ) : (
              sorted.map((player) => {
                const isSelected = selectedPlayers.includes(player.PlayerXID);
                const isAllTournament = Boolean(player.All_Tournament);
                return (
                  <tr key={player.PlayerXID} className={isSelected ? "row-selected" : ""}>
                    <td>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleSelectPlayer(player.PlayerXID)}
                      />
                    </td>
                    <td className="att-indicator">{isAllTournament ? "★" : ""}</td>
                    <td>{player.JerseyNumber}</td>
                    <td>{player.LegalFirstName}</td>
                    <td>{player.LastName}</td>
                    <td>{player.PositionName}</td>
                    <td>{player.FirstChoiceVotes || 0}</td>
                    <td>{player.SecondChoiceVotes || 0}</td>
                    <td>{player.ThirdChoiceVotes || 0}</td>
                    <td>{player.CoachVotes || 0}</td>
                    <td>{getOverallScore(player)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="watching-page">
      <div className="watch-top-banner">
        <div className="watch-sort-controls">
          <label htmlFor="watch-sort-column">Sort By:</label>
          <select
            id="watch-sort-column"
            value={sortColumn}
            onChange={(e) => setSortColumn(e.target.value)}
          >
            {SORT_COLUMNS.map((column) => (
              <option key={column.value} value={column.value}>
                {column.label}
              </option>
            ))}
          </select>
          <button
            className="toolbar-btn toolbar-btn-att"
            onClick={() => setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))}
          >
            {sortDirection === "asc" ? "Asc" : "Desc"}
          </button>
        </div>

        <div className="watch-att-controls">
          <button
            className="toolbar-btn toolbar-btn-att"
            disabled={selectedPlayers.length === 0}
            onClick={handleAddSelectedToAllTournament}
          >
            Add Selected to All Tournament
          </button>
          {actionStatus && (
            <span className={`action-status ${actionStatus.isError ? "status-error" : "status-success"}`}>
              {actionStatus.msg}
            </span>
          )}
        </div>
      </div>

      <div className="watch-team-selection-row">
        <div className="watch-team-selector-panel">
          <label htmlFor="watch-team-1">Team 1: </label>
          <select
            id="watch-team-1"
            value={team1Id}
            onChange={(e) => handleTeamSelect(1, e.target.value)}
          >
            <option value="">-- Select Team --</option>
            {watchedTeams.map((team) => (
              <option key={team.TeamXID} value={team.TeamXID}>
                {team.TeamName}
              </option>
            ))}
          </select>
        </div>

        <button
          className="watch-list-icon-btn toolbar-btn toolbar-btn-att"
          onClick={() => setShowWatchListModal(true)}
          title="Open watch list"
        >
          ☰
        </button>

        <div className="watch-team-selector-panel">
          <label htmlFor="watch-team-2">Team 2: </label>
          <select
            id="watch-team-2"
            value={team2Id}
            onChange={(e) => handleTeamSelect(2, e.target.value)}
          >
            <option value="">-- Select Team --</option>
            {watchedTeams.map((team) => (
              <option key={team.TeamXID} value={team.TeamXID}>
                {team.TeamName}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="watching-two-column">
        {renderTeamRoster(team1Id, "Team 1", showNoVotesTeam1, setShowNoVotesTeam1)}
        {renderTeamRoster(team2Id, "Team 2", showNoVotesTeam2, setShowNoVotesTeam2)}
      </div>

      {showWatchListModal && (
        <div className="watch-list-modal-overlay" onClick={() => setShowWatchListModal(false)}>
          <div className="watch-list-modal" onClick={(e) => e.stopPropagation()}>
            <div className="watch-list-modal-header">
              <h3>Watch List Teams</h3>
              <button
                className="toolbar-btn toolbar-btn-clear"
                onClick={() => setShowWatchListModal(false)}
              >
                Close
              </button>
            </div>
            {watchedTeams.length === 0 ? (
              <div className="watch-list-empty">No teams added yet.</div>
            ) : (
              <div className="watch-list-chip-row">
                {watchedTeams.map((team) => (
                  <span key={team.TeamXID} className="watch-list-chip">
                    {team.TeamName}
                    <button
                      onClick={() => removeTeamFromWatchList(team.TeamXID)}
                      title="Remove from watch list"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default WatchingPage;
